import React from 'react';
import { Container, Paper, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta('Privacy Policy — Seriously', 'How Seriously handles your personal data. GDPR-compliant privacy policy covering data collection, storage, and your rights.');

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>1. Data Controller</Typography>
          <Typography variant="body1" paragraph>
            Seriously ("we", "us", "our") is the data controller for the personal data processed through this application. For questions about your data, contact us at info@dateseriously.com.
          </Typography>

          <Typography variant="h6" gutterBottom>2. Data We Collect</Typography>
          <Typography variant="body1" paragraph>
            We collect and process the following categories of personal data:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li><strong>Account data:</strong> password (stored as a cryptographic hash), date of birth, account creation date.</li>
            <li><strong>Profile data:</strong> profile photo, voice recording, self-description tags, location and search radius.</li>
            <li><strong>Biometric data (special category):</strong> we generate mathematical embeddings (numerical representations) from your photo and voice recording. These are used solely for compatibility matching and are not used to identify you.</li>
            <li><strong>Usage data:</strong> interactions (likes, skips), voice messages sent to other users, voice feedback.</li>
            <li><strong>Technical data:</strong> session tokens stored in your browser's localStorage for authentication.</li>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>3. Legal Basis for Processing</Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li><strong>Consent (Art. 6(1)(a) and Art. 9(2)(a) GDPR):</strong> processing of your biometric data (photo and voice embeddings) is based on your explicit consent, given at account creation.</li>
            <li><strong>Contract (Art. 6(1)(b) GDPR):</strong> processing necessary to provide the dating service (profile display, matching, messaging).</li>
            <li><strong>Legitimate interest (Art. 6(1)(f) GDPR):</strong> security measures (rate limiting, session management).</li>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>4. Third-Party Processors</Typography>
          <Typography variant="body1" paragraph>
            Your data is processed by the following sub-processors:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li><strong>Amazon Web Services (AWS S3):</strong> stores your photos, voice recordings, and voice messages. Region: US East.</li>
            <li><strong>MongoDB Atlas:</strong> stores your profile data, interactions, and embeddings.</li>
            <li><strong>Replicate:</strong> receives your photo and voice recording temporarily to compute embeddings. Data is not retained by Replicate after processing.</li>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>5. Data Retention</Typography>
          <Typography variant="body1" paragraph>
            Your data is retained for as long as your account is active. You may delete your account at any time, which will permanently remove all your data from our systems (see Your Rights below).
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>6. Your Rights</Typography>
          <Typography variant="body1" paragraph>
            Under GDPR, you have the following rights:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li><strong>Right of access (Art. 15):</strong> you can export all your data from the Profile page.</li>
            <li><strong>Right to erasure (Art. 17):</strong> you can delete your account and all associated data from the Profile page.</li>
            <li><strong>Right to data portability (Art. 20):</strong> you can download your data in a machine-readable format (JSON).</li>
            <li><strong>Right to withdraw consent:</strong> you may withdraw your biometric data consent at any time by deleting your account.</li>
            <li><strong>Right to object / restrict processing:</strong> contact us at info@dateseriously.com.</li>
            <li><strong>Right to lodge a complaint:</strong> you have the right to lodge a complaint with your local data protection authority.</li>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>7. Cookies and Local Storage</Typography>
          <Typography variant="body1" paragraph>
            We do not use cookies. We use your browser's localStorage exclusively for session authentication (user ID and session token). This is strictly necessary for the service to function and is exempt from consent requirements under the ePrivacy Directive. The map component loads tiles from OpenStreetMap, which may log your IP address.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>8. International Transfers</Typography>
          <Typography variant="body1" paragraph>
            Your data may be transferred to and processed in the United States (AWS, Replicate). These transfers are covered by Standard Contractual Clauses or equivalent safeguards.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>9. Security</Typography>
          <Typography variant="body1" paragraph>
            We protect your data with: bcrypt password hashing, session token authentication, rate limiting, encrypted storage (S3, MongoDB Atlas TLS), and upload size limits. No system is 100% secure, and we encourage you to use a strong password.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>10. Changes to This Policy</Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. Significant changes will be communicated through the application.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
